import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { playClick } from '../utils/SoundManager';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * بطاقة ذكية تظهر أهم إجراء مطلوب من المستخدم الآن
 * أولويات (من الأعلى للأدنى):
 * 1. طلبات تواصل تنتظر الرد
 * 2. زوار جدد لم يُشاهَدوا
 * 3. إعجابات جديدة
 * 4. ادعُ صديقاً (إذا لم يدعو أحداً)
 * 5. أكمل ملفك (إذا غير مكتمل)
 */
function SmartHighlightCard({ user, pendingRequests = 0, profileCompletion = 100 }) {
  const navigate = useNavigate();
  const [badges, setBadges] = useState({ new_views: 0, new_likes: 0 });
  const [referralCount, setReferralCount] = useState(null);

  useEffect(() => {
    const uid = localStorage.getItem('userId');
    const headers = uid ? { 'X-User-Id': uid } : {};
    
    axios.get(`${BACKEND_URL}/api/stats/badges`, { withCredentials: true, headers })
      .then(r => setBadges(r.data))
      .catch(() => {});

    axios.get(`${BACKEND_URL}/api/referral/my-code`, { withCredentials: true, headers })
      .then(r => setReferralCount(r.data.referral_count || 0))
      .catch(() => {});
  }, []);

  // اختيار البطاقة بحسب الأولوية
  let card = null;

  if (pendingRequests > 0) {
    card = {
      key: 'requests',
      icon: '💌',
      title: `لديك ${pendingRequests} ${pendingRequests === 1 ? 'طلب تواصل' : 'طلبات تواصل'} في انتظار ردك`,
      subtitle: 'لا تجعلهم ينتظرون طويلاً - الرد السريع يزيد التفاعل',
      ctaText: 'استعرض الطلبات الآن',
      onClick: () => navigate('/messages'),
      gradient: 'linear-gradient(135deg, #FF6B9D 0%, #C06EED 100%)',
      pulseColor: 'rgba(255, 107, 157, 0.4)'
    };
  } else if (badges.new_views > 0) {
    card = {
      key: 'views',
      icon: '👁️',
      title: `${badges.new_views} ${badges.new_views === 1 ? 'شخص شاهد' : 'أشخاص شاهدوا'} ملفك الشخصي!`,
      subtitle: 'تعرّف عليهم - ربما أحدهم هو شريك حياتك',
      ctaText: 'شاهد من زارك',
      onClick: () => navigate('/stats?tab=views'),
      gradient: 'linear-gradient(135deg, #1DA1F2 0%, #0E76B8 100%)',
      pulseColor: 'rgba(29, 161, 242, 0.4)'
    };
  } else if (badges.new_likes > 0) {
    card = {
      key: 'likes',
      icon: '❤️',
      title: `${badges.new_likes} ${badges.new_likes === 1 ? 'شخص أعجب' : 'أشخاص أعجبوا'} بملفك`,
      subtitle: 'بادر بالتواصل معهم قبل أن تفوت الفرصة',
      ctaText: 'اكتشف من أُعجب بك',
      onClick: () => navigate('/stats?tab=likes'),
      gradient: 'linear-gradient(135deg, #e91e63 0%, #c2185b 100%)',
      pulseColor: 'rgba(233, 30, 99, 0.4)'
    };
  } else if (profileCompletion < 80) {
    card = {
      key: 'profile',
      icon: '📝',
      title: `ملفك مكتمل ${profileCompletion}% فقط`,
      subtitle: 'الملفات المكتملة تحصل على زيارات أكثر بـ 3 أضعاف! أكمل ملفك الآن',
      ctaText: 'أكمل ملفي الشخصي',
      onClick: () => navigate('/profile-setup'),
      gradient: 'linear-gradient(135deg, #FFA500 0%, #FF6B35 100%)',
      pulseColor: 'rgba(255, 165, 0, 0.4)'
    };
  } else if (referralCount === 0) {
    card = {
      key: 'referral',
      icon: '🎁',
      title: 'ادعُ صديقاً واكسب 7 أيام Premium مجاناً',
      subtitle: 'كل صديق ينضم بكودك يمنحك أسبوعاً كاملاً من الميزات الحصرية',
      ctaText: 'ابدأ بدعوة الأصدقاء',
      onClick: () => navigate('/referral'),
      gradient: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
      pulseColor: 'rgba(255, 165, 0, 0.4)'
    };
  } else {
    // البطاقة الافتراضية: تشجيع على البحث
    card = {
      key: 'default',
      icon: '✨',
      title: `أهلاً ${user?.name || 'بك'}! ابدأ يومك ببحث جديد`,
      subtitle: 'ربما يكون اليوم هو اليوم الذي تجد فيه شريك حياتك',
      ctaText: 'ابحث الآن',
      onClick: () => navigate('/search'),
      gradient: 'linear-gradient(135deg, #00897B 0%, #00B4A2 100%)',
      pulseColor: 'rgba(0, 137, 123, 0.3)'
    };
  }

  return (
    <div
      data-testid={`smart-card-${card.key}`}
      onClick={() => { playClick(); card.onClick(); }}
      style={{
        background: card.gradient,
        borderRadius: '20px',
        padding: '22px 24px',
        marginBottom: '24px',
        cursor: 'pointer',
        position: 'relative',
        overflow: 'hidden',
        boxShadow: `0 8px 28px ${card.pulseColor}`,
        transition: 'transform 0.25s ease, box-shadow 0.25s ease',
        display: 'flex',
        alignItems: 'center',
        gap: '20px',
        flexWrap: 'wrap'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-3px)';
        e.currentTarget.style.boxShadow = `0 12px 36px ${card.pulseColor}`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = `0 8px 28px ${card.pulseColor}`;
      }}
    >
      {/* Shine effect */}
      <div style={{
        position: 'absolute',
        top: '-50%',
        right: '-20%',
        width: '300px',
        height: '300px',
        background: 'radial-gradient(circle, rgba(255,255,255,0.18) 0%, transparent 70%)',
        pointerEvents: 'none'
      }} />

      <div
        className="heartbeat"
        style={{
          fontSize: '52px',
          flexShrink: 0,
          background: 'rgba(255, 255, 255, 0.18)',
          borderRadius: '50%',
          width: '80px',
          height: '80px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backdropFilter: 'blur(4px)',
          zIndex: 1
        }}
      >
        {card.icon}
      </div>

      <div style={{ flex: 1, minWidth: '200px', zIndex: 1 }}>
        <h3
          style={{
            fontSize: '18px',
            fontWeight: '800',
            color: 'white',
            margin: 0,
            marginBottom: '6px',
            lineHeight: 1.5,
            textShadow: '0 2px 6px rgba(0, 0, 0, 0.2)'
          }}
        >
          {card.title}
        </h3>
        <p
          style={{
            fontSize: '13px',
            color: 'rgba(255, 255, 255, 0.92)',
            margin: 0,
            lineHeight: 1.7
          }}
        >
          {card.subtitle}
        </p>
      </div>

      <button
        data-testid="smart-card-cta"
        style={{
          background: 'rgba(255, 255, 255, 0.22)',
          backdropFilter: 'blur(8px)',
          border: '1px solid rgba(255, 255, 255, 0.35)',
          color: 'white',
          padding: '12px 22px',
          borderRadius: '12px',
          fontSize: '14px',
          fontWeight: '700',
          cursor: 'pointer',
          whiteSpace: 'nowrap',
          zIndex: 1,
          transition: 'background 0.2s'
        }}
        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.32)'}
        onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.22)'}
      >
        {card.ctaText} ←
      </button>
    </div>
  );
}

export default SmartHighlightCard;
